import React, { Component } from 'react';
import { bool, func } from 'prop-types';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { formatDateFullFormat } from '@widergy/web-utils/lib/date';

import { ROUTES } from 'constants/routes';
import { actionCreators as userFormActionCreators, formResponsePropTypes } from 'redux/userFormHandlers';
import { actionCreators as accountActions, accountPropTypes } from 'redux/accountHandlers';
import { PROCEDURE } from 'constants/userForms';
import SecondaryTopbar from 'app/components/SecondaryTopbar';
import AccountSelect from 'app/components/AccountSelect';
import UserFormCreator from 'app/components/UserFormCreator';
import withPagination from 'app/components/UTPagination/HOCs/pagination';
import WithoutAccounts from 'app/components/WithoutAccounts';
import { message } from 'utils/stringUtils';
import { appConfig } from 'config/customization';
import { gaTrackPageView } from 'services/AnalyticsService';

import ProceduresScene from './layout';
import styles from './styles';

class ProceduresSceneContainer extends Component {
  componentDidMount() {
    this.props.getFormCategories();
    gaTrackPageView(ROUTES.PROCEDURES);
  }

  handleChangeAccount = account => {
    this.props.changeAccount(account);
    this.props.onMount();
  };

  render() {
    const { rows, onCancel, onReiteration, onRowsRendered } = this.props;

    return (
      <div style={styles.container}>
        <SecondaryTopbar titleText={message(`FieldDetails:${PROCEDURE}`)}>
          {!this.props.isPublic && appConfig.procedures.showAccountSelector && (
            <AccountSelect
              filterPlaceholder={message('AccountStatus:filterPlaceholder')}
              selectedOption={this.props.currentAccount}
              dualLabel={['client_number', 'address']}
              options={this.props.accounts}
              onSelect={this.handleChangeAccount}
              keyProp="id"
              style={styles.selectContainer}
            />
          )}
          {this.props.isPublic && (
            <UserFormCreator formType={PROCEDURE} handleFormSelected={this.props.generateProcedure} />
          )}
        </SecondaryTopbar>
        {!this.props.isPublic && (
          <ProceduresScene
            loading={this.props.loading}
            procedures={rows}
            onCancel={onCancel}
            onReiteration={onReiteration}
            onRowsRendered={onRowsRendered}
            appending={this.props.appending}
          />
        )}
      </div>
    );
  }
}

const procedureSelector = createSelector(
  store => store.userForm[`${PROCEDURE}FormResponses`],
  procedures =>
    procedures.map(procedure => ({
      ...procedure,
      started_at: formatDateFullFormat(procedure.started_at),
      status: {
        description: message(`FieldDetails:${procedure.status}`),
        isPending: message(`FieldDetails:${procedure.status}`) === message('FieldDetails:pending')
      },
      form_description: { form_name: procedure.form_name, form_category_name: procedure.form_category_name }
    }))
);

const mapStateToProps = store => ({
  rows: procedureSelector(store),
  loading: store.userForm[`${PROCEDURE}FormResponsesLoading`],
  accounts: store.account.accounts,
  accountsLoading: store.account.accountsLoading,
  currentAccount: store.account.currentAccount,
  formCategories: store.userForm.procedureFormCategories,
  appending: store.account.appendFormResponsesLoading,
  accountsError: store.account.accountsError
});

const mapDispatchToProps = (dispatch, ownProps) => {
  if (!ownProps.isPublic) {
    return {
      onMount: pageSize => dispatch(userFormActionCreators.getFormResponses({ type: PROCEDURE, pageSize })),
      onCancel: id => dispatch(userFormActionCreators.cancelation(PROCEDURE, id)),
      onReiteration: id => dispatch(userFormActionCreators.reiteration(PROCEDURE, id)),
      append: (pageNumber, pageSize) =>
        dispatch(userFormActionCreators.appendFormResponses({ type: PROCEDURE, pageNumber, pageSize })),
      getFormCategories: () => dispatch(userFormActionCreators.getFormCategories(PROCEDURE)),
      changeAccount: account => dispatch(accountActions.changeCurrentAccount(account)),
      generateProcedure: formId =>
        dispatch(userFormActionCreators.createForm(formId, PROCEDURE, ROUTES.NEW_FORM))
    };
  }
  return {
    onMount: () => {},
    append: () => {},
    getFormCategories: () => dispatch(userFormActionCreators.getPublicFormCategories(PROCEDURE)),
    generateProcedure: formId =>
      dispatch(userFormActionCreators.createForm(formId, PROCEDURE, ROUTES.PUBLIC_NEW_FORM))
  };
};

ProceduresSceneContainer.propTypes = {
  rows: formResponsePropTypes,
  onCancel: func,
  onReiteration: func,
  onRowsRendered: func,
  getFormCategories: func,
  changeAccount: func,
  onMount: func.isRequired,
  accounts: accountPropTypes,
  currentAccount: accountPropTypes,
  generateProcedure: func,
  isPublic: bool,
  loading: bool,
  appending: bool
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  WithoutAccounts({
    shouldShowMessage: props => props.accounts.length === 0 && !props.accountsLoading && !props.isPublic,
    accountsError: props => props.accountsError
  })(withPagination(ProceduresSceneContainer, 20))
);
